import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { getQuestions } from '../api/client'
import type { ForecastQuestion } from '../types'

export default function HistoryPage() {
  const navigate = useNavigate()
  const [questions, setQuestions] = useState<ForecastQuestion[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [query, setQuery] = useState('')

  useEffect(() => {
    loadHistory()
  }, [])

  const loadHistory = async () => {
    try {
      setIsLoading(true)
      const qs = await getQuestions()
      setQuestions(qs)
    } catch (error) {
      console.error('Failed to load history:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return questions
    return questions.filter((question) => {
      const text = `${question.formatted_question || ''} ${question.original_question || ''} ${question.resolution_criteria || ''}`.toLowerCase()
      return text.includes(q)
    })
  }, [questions, query])

  const handleQuestionClick = (questionId: string) => {
    navigate(`/results/${questionId}`)
  }

  if (isLoading) {
    return (
      <div className="min-h-screen px-4 sm:px-6 py-6 sm:py-8">
        <div className="mx-auto max-w-7xl">
          <p className="text-xs text-muted">Loading history…</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen px-4 sm:px-6 py-6 sm:py-8">
      <div className="mx-auto max-w-7xl">
        <h1 className="font-serif font-semibold text-2xl sm:text-3xl text-ink">Forecast history</h1>

        {questions.length > 0 && (
          <div className="mt-4">
            <input
              className="w-full rounded-xl border border-rule bg-paper px-3 py-2.5 text-sm text-ink shadow-[inset_0_1px_0_rgba(20,18,16,.05)] focus:outline-none focus:ring-2 focus:ring-green/30 focus:border-green/60"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search questions and resolution criteria…"
            />
          </div>
        )}

        {questions.length === 0 ? (
          <div className="mt-4 rounded-delphi border border-rule bg-paper2 shadow-delphi p-4">
            <p className="text-sm text-muted">No forecasts yet. Start a new forecast to see it here.</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="mt-4 rounded-delphi border border-rule bg-paper2 shadow-delphi p-4">
            <p className="text-sm text-muted">No forecasts match your search.</p>
          </div>
        ) : (
          <div className="mt-4 flex flex-col gap-3">
            {filtered.map((question) => (
              <button
                key={question.question_id}
                onClick={() => handleQuestionClick(question.question_id)}
                type="button"
                className="text-left rounded-delphi border border-rule bg-paper2 shadow-delphi px-4 py-3 hover:bg-white/30 transition"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-serif font-semibold text-ink leading-snug">
                      {question.formatted_question}
                    </div>
                  </div>
                  <div className="shrink-0 flex flex-col items-end gap-2">
                    {typeof question.last_probability === 'number' ? (
                      <div className="font-serif text-2xl text-ink leading-none">
                        {(question.last_probability * 100).toFixed(1)}%
                      </div>
                    ) : (
                      <div className="font-serif text-2xl text-muted leading-none">--</div>
                    )}
                    <span className="text-[11px] text-muted border border-rule rounded-full px-2 py-1 bg-paper">
                      {(question.run_count ?? question.runs?.length ?? 0)} run
                      {(question.run_count ?? question.runs?.length ?? 0) !== 1 ? 's' : ''}
                    </span>
                  </div>
                </div>
                {question.resolution_criteria && (
                  <p className="mt-2 text-sm text-muted line-clamp-3">{question.resolution_criteria}</p>
                )}
                <div className="mt-2 font-mono text-[11px] text-muted">
                  <span>Created: {new Date(question.created_at).toLocaleString()}</span>
                  {question.last_run_at && (
                    <span className="ml-3">
                      Last run: {new Date(question.last_run_at).toLocaleString()}
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

